import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { logger } from '@/core/services/logger';
import { supabase } from '@/core/services/supabase';
import { Database } from '@/core/services/supabase/types';
import { useAuth } from '@/core/auth/AuthProvider';
import { setErrorLoggerScope } from '@/core/api/errorLoggerScope';

/**
 * Organization entity
 * 
 * Per PLATFORM_DICTIONARY.md:
 * - 'platform' = Mobul itself (top-level)
 * - 'agency' = Marketing agency/reseller using Mobul
 * 
 * Note: 'internal' is legacy, should be 'platform'
 */
interface Organization {
  id: string;
  name: string;
  type: 'internal' | 'agency' | 'platform'; // 'internal' is legacy for 'platform'
  settings_json: unknown;
}

/**
 * Client entity
 * 
 * Per PLATFORM_DICTIONARY.md:
 * A business that the Agency serves. The end customer of the Agency.
 * Example: "Joe's Auto Dealership" (client of ABC Marketing Agency)
 * 
 * NOT to be confused with:
 * - Customer (person who receives mail)
 * - API client (software)
 */
interface Client {
  id: string;
  org_id: string;
  name: string;
  industry: Database['public']['Enums']['industry_type'];
  timezone: string;
  logo_url: string | null;
  brand_colors_json: unknown;
  // Bug #34 fix: add missing fields used by CompanySettings
  tagline?: string;
  website_url?: string;
  app_domain?: string;
  support_email?: string;
  support_phone?: string;
  address?: string;
}

export type ImpersonatedRole = 'admin' | 'agency_owner' | 'company_owner' | null;

interface TenantContextType {
  organizations: Organization[];
  clients: Client[];
  currentOrg: Organization | null;
  currentClient: Client | null;
  setCurrentOrg: (org: Organization | null) => void;
  setCurrentClient: (client: Client | null) => void;
  loading: boolean;
  isAdminMode: boolean;
  setAdminMode: (mode: boolean) => void;
  impersonatedRole: ImpersonatedRole;
  setImpersonatedRole: (role: ImpersonatedRole) => void;
  effectiveRole: 'admin' | 'agency_owner' | 'company_owner';
  refetchTenantData: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, hasRole } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdminMode, setIsAdminMode] = useState(() => {
    const stored = localStorage.getItem('adminMode');
    return stored === 'true';
  });
  const [impersonatedRole, setImpersonatedRoleState] = useState<ImpersonatedRole>(() => {
    const stored = localStorage.getItem('impersonatedRole');
    if (stored === 'admin' || stored === 'agency_owner' || stored === 'company_owner') {
      return stored;
    }
    return null;
  });

  // Refs for stable access in handlers (avoids stale closure bugs)
  const clientsRef = useRef<Client[]>([]);
  const organizationsRef = useRef<Organization[]>([]);
  clientsRef.current = clients;
  organizationsRef.current = organizations;

  useEffect(() => {
    if (!user) {
      setOrganizations([]);
      setClients([]);
      setCurrentOrg(null);
      setCurrentClient(null);
      setLoading(false);
      return;
    }

    fetchTenantData();
  }, [user]);

  useEffect(() => {
    setErrorLoggerScope(currentOrg?.id ?? null, currentClient?.id ?? null);
  }, [currentOrg?.id, currentClient?.id]);

  const fetchTenantData = async () => {
    try {
      setLoading(true);

      // Fetch organizations
      const { data: orgsData, error: orgsError } = await supabase
        .from('organizations')
        .select('*');
      if (orgsError) throw orgsError;

      const allOrgs = orgsData || [];
      setOrganizations(allOrgs);

      // Fetch clients
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*');
      if (clientsError) throw clientsError;

      const allClients = clientsData || [];
      setClients(allClients);

      // Restore saved selections from localStorage
      const savedOrgId = localStorage.getItem('currentOrgId');
      const savedClientId = localStorage.getItem('currentClientId');

      let restoredOrg: Organization | null = null;
      let restoredClient: Client | null = null;

      // Restore client first (it determines the org)
      if (savedClientId) {
        restoredClient = allClients.find(c => c.id === savedClientId) || null;
      }

      // Restore org — if client was restored, ensure org matches client
      if (restoredClient) {
        restoredOrg = allOrgs.find(o => o.id === restoredClient!.org_id) || null;
      } else if (savedOrgId) {
        restoredOrg = allOrgs.find(o => o.id === savedOrgId) || null;
      }

      // Fallback: pick first org if nothing restored
      if (!restoredOrg && allOrgs.length > 0) {
        restoredOrg = allOrgs[0];
      }

      // Fallback: auto-select first client of the org for agency/company owners
      if (!restoredClient && restoredOrg && allClients.length > 0) {
        if (hasRole('agency_owner') || hasRole('company_owner')) {
          restoredClient = allClients.find(c => c.org_id === restoredOrg!.id) || null;
        }
      }

      // Apply restored selections
      setCurrentOrg(restoredOrg);
      setCurrentClient(restoredClient);

      // Persist to localStorage
      if (restoredOrg) {
        localStorage.setItem('currentOrgId', restoredOrg.id);
      }
      if (restoredClient) {
        localStorage.setItem('currentClientId', restoredClient.id);
      }

    } catch (error) {
      logger.error('Error fetching tenant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetCurrentOrg = (org: Organization | null) => {
    setCurrentOrg(org);
    if (org) {
      localStorage.setItem('currentOrgId', org.id);
    } else {
      localStorage.removeItem('currentOrgId');
    }
    // Reset client to the first client of the new org (use ref for fresh data)
    const latestClients = clientsRef.current;
    if (org && latestClients.length > 0) {
      const orgClients = latestClients.filter(c => c.org_id === org.id);
      const firstClient = orgClients[0] || null;
      setCurrentClient(firstClient);
      if (firstClient) {
        localStorage.setItem('currentClientId', firstClient.id);
      } else {
        localStorage.removeItem('currentClientId');
      }
    } else {
      setCurrentClient(null);
      localStorage.removeItem('currentClientId');
    }
  };

  // Fix 1: When selecting a client, also sync the org to match
  const handleSetCurrentClient = (client: Client | null) => {
    setCurrentClient(client);
    if (client) {
      localStorage.setItem('currentClientId', client.id);

      // Sync org to match the client's parent organization
      const latestOrgs = organizationsRef.current;
      const clientOrg = latestOrgs.find(o => o.id === client.org_id);
      if (clientOrg) {
        setCurrentOrg(clientOrg);
        localStorage.setItem('currentOrgId', clientOrg.id);
      }

      // When selecting a client, exit admin mode
      setIsAdminMode(false);
      localStorage.setItem('adminMode', 'false');
    } else {
      localStorage.removeItem('currentClientId');
    }
  };

  // Fix 3: Persist selection across Admin/Client view switches
  const handleSetAdminMode = (mode: boolean) => {
    setIsAdminMode(mode);
    localStorage.setItem('adminMode', mode.toString());

    if (mode) {
      // Entering admin mode: don't clear client — just hide the selectors in UI.
      // The client/org remain stored in localStorage so returning restores them.
      // We DO clear currentClient from context so pages show admin views,
      // but we don't wipe localStorage so it restores when exiting admin mode.
      setCurrentClient(null);
      // Note: we intentionally do NOT call localStorage.removeItem('currentClientId')
    } else {
      // Exiting admin mode: restore client/org from localStorage
      const savedClientId = localStorage.getItem('currentClientId');
      const savedOrgId = localStorage.getItem('currentOrgId');
      const latestClients = clientsRef.current;
      const latestOrgs = organizationsRef.current;

      if (savedClientId) {
        const savedClient = latestClients.find(c => c.id === savedClientId) || null;
        if (savedClient) {
          setCurrentClient(savedClient);
          // Also ensure org matches
          const clientOrg = latestOrgs.find(o => o.id === savedClient.org_id) || null;
          if (clientOrg) {
            setCurrentOrg(clientOrg);
          }
        }
      } else if (savedOrgId) {
        const savedOrg = latestOrgs.find(o => o.id === savedOrgId) || null;
        if (savedOrg) {
          setCurrentOrg(savedOrg);
          // Auto-select first client of the org
          const orgClients = latestClients.filter(c => c.org_id === savedOrg.id);
          if (orgClients.length > 0) {
            setCurrentClient(orgClients[0]);
            localStorage.setItem('currentClientId', orgClients[0].id);
          }
        }
      }
    }
  };

  const handleSetImpersonatedRole = (role: ImpersonatedRole) => {
    setImpersonatedRoleState(role);
    if (role) {
      localStorage.setItem('impersonatedRole', role);
    } else {
      localStorage.removeItem('impersonatedRole');
    }

    if (role === 'admin') {
      setIsAdminMode(true);
      localStorage.setItem('adminMode', 'true');
      setCurrentClient(null);
    } else if (role === 'agency_owner' || role === 'company_owner') {
      setIsAdminMode(false);
      localStorage.setItem('adminMode', 'false');
      const savedClientId = localStorage.getItem('currentClientId');
      const latestClients = clientsRef.current;
      const latestOrgs = organizationsRef.current;
      if (savedClientId) {
        const savedClient = latestClients.find(c => c.id === savedClientId) || null;
        if (savedClient) {
          setCurrentClient(savedClient);
          const clientOrg = latestOrgs.find(o => o.id === savedClient.org_id) || null;
          if (clientOrg) setCurrentOrg(clientOrg);
        }
      }
    } else {
      localStorage.removeItem('impersonatedRole');
    }
  };

  const effectiveRole: 'admin' | 'agency_owner' | 'company_owner' = (() => {
    if (impersonatedRole) return impersonatedRole;
    if (hasRole('admin')) return 'admin';
    if (hasRole('agency_owner')) return 'agency_owner';
    return 'company_owner';
  })();

  return (
    <TenantContext.Provider
      value={{
        organizations,
        clients,
        currentOrg,
        currentClient,
        setCurrentOrg: handleSetCurrentOrg,
        setCurrentClient: handleSetCurrentClient,
        loading,
        isAdminMode,
        setAdminMode: handleSetAdminMode,
        impersonatedRole,
        setImpersonatedRole: handleSetImpersonatedRole,
        effectiveRole,
        refetchTenantData: fetchTenantData,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
